import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  Input,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {ExtraDateControlValidators} from '@atlas-angular/businesstypes';
import {coerceBooleanPrimitive, coerceText} from '@atlas-angular/cdk/coercion';
import {takeUntilDestroyed, UntilDestroy} from '@atlas-angular/rxjs';
import {Date, DateKey, DateUtils, isValidDate, validateDate, Text} from '@atlas/businesstypes';
import {DropdownHost, DropdownPosition, DropdownTemplateContext} from '@maia/dropdowns';
import {provideNestedInputContainer} from '@maia/forms';
import {DateFormat, DEFAULT_DATE_FORMAT} from '@maia/input-masks';
import {ModalConfirmedResult, ModalResolution, ModalResult} from '@maia/modals';
import {filter, finalize, map, tap} from 'rxjs/operators';

import {DatepickerVisibleView} from '../../pickers/datepicker/datepicker.component';
import {MAXIMUM_DROPDOWN_WIDTH, MINIMUM_DROPDOWN_WIDTH} from '../constants';

/**
 * Unique object meant to signify "no value has recently been set using writeValue"
 */
const NO_WRITTEN_VALUE = {};

/**
 * A date input. This input provides an input with input mask where a date can be typed and a button
 * to open a dropdown which allows selecting a date.
 *
 * @ngModule DatePickersModule
 */
@Component({
  selector: 'maia-input-date',
  templateUrl: './input-date.component.html',
  styleUrls: ['./input-date.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    provideNestedInputContainer(),
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputDateComponent),
      multi: true,
    },

    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => InputDateComponent),
      multi: true,
    },
  ],
})
@UntilDestroy()
export class InputDateComponent implements ControlValueAccessor, Validator {
  /**
   * The current value, be it complete or incomplete
   */
  public readonly control: FormControl;

  @Input()
  @coerceBooleanPrimitive()
  public useCustomPlaceholder = false;

  @Input()
  @coerceText()
  public customPlaceholderText?: Text | string = undefined;

  @ViewChild('dropdownHost', {static: true})
  public _dropdownHost: DropdownHost;

  @ViewChild('dropdownContent', {static: true})
  public dropdownContent: TemplateRef<DropdownTemplateContext<Date>>;

  private _minimum?: Date;
  private _minimumValidator: ValidatorFn;

  private _maximum?: Date;
  private _maximumValidator: ValidatorFn;

  private _dateFormat: DateFormat = DEFAULT_DATE_FORMAT;

  @Input()
  public set dateFormat(dateFormat: DateFormat) {
    if (dateFormat == null) {
      return;
    }

    this._dateFormat = dateFormat;
  }

  public get dateFormat(): DateFormat {
    return this._dateFormat;
  }

  /**
   * The initial year/month combination to open.
   */
  @Input()
  public initialValue?: Date;

  /**
   * The view to open the datepicker on.
   */
  @Input()
  public visibleView?: DatepickerVisibleView;

  public _disabled = false;

  private _open = false;

  private _recentlyWrittenValue: unknown = NO_WRITTEN_VALUE;

  public constructor(fb: FormBuilder, private readonly _changeDetector: ChangeDetectorRef) {
    this.control = fb.control(undefined);
  }

  // istanbul ignore next: provided by [(ngModel)]
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private _onValidatorChanged = () => {};

  // istanbul ignore next: provided by [(ngModel)]
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private _onTouched = () => {};

  public showCustomPlaceholder(): boolean {
    return this.useCustomPlaceholder && DateUtils.today().equals(this.value);
  }

  /**
   * The last allowed date.
   *
   * While it's technically fine to have a maximum date that lies before the minimum date, this
   * would render the user unable to select a date.
   */
  public get maximum(): Date | undefined {
    return this._maximum;
  }

  @Input()
  public set maximum(maximum: Date | undefined) {
    if (maximum != null && !Date.isDate(maximum)) {
      maximum = new Date(maximum);
    }

    this._maximum = maximum;
    this._maximumValidator =
      maximum != null
        ? ExtraDateControlValidators.before(DateUtils.add(maximum, DateKey.Day, 1))
        : // eslint-disable-next-line @typescript-eslint/unbound-method
          Validators.nullValidator;
    this._onValidatorChanged();
  }
  /**
   * The first allowed date.
   *
   * While it's technically fine to have a minimum date that lies after the maximum date, this would
   * render the user unable to select a date.
   */
  @Input()
  public get minimum(): Date | undefined {
    return this._minimum;
  }

  public set minimum(minimum: Date | undefined) {
    if (minimum != null && !Date.isDate(minimum)) {
      minimum = new Date(minimum);
    }

    this._minimum = minimum;
    this._minimumValidator =
      minimum != null
        ? ExtraDateControlValidators.after(DateUtils.subtract(minimum, DateKey.Day, 1))
        : // eslint-disable-next-line @typescript-eslint/unbound-method
          Validators.nullValidator;
    this._onValidatorChanged();
  }

  public get value(): Date | null | undefined {
    return this.control.value as Date | null | undefined;
  }

  /**
   * Opens a datepicker in a dropdown.
   */
  public openDropdown(): false {
    if (this._disabled || this._open) {
      return false;
    }

    this._open = true;

    this._dropdownHost
      .prepareTemplate(this.dropdownContent, {
        position: DropdownPosition.BOTTOM_ALIGNED,
        alignedDropdownExtra: {
          maximumSize: MAXIMUM_DROPDOWN_WIDTH,
          minimumSize: MINIMUM_DROPDOWN_WIDTH,
          alternativePosition: DropdownPosition.BOTTOM_LEFT,
        },
      })
      .pipe(
        finalize(() => (this._open = false)),
        takeUntilDestroyed(this),
        // call onTouched before filtering on confirmation
        tap(() => this._onTouched()),
        filter<ModalResult<Date>, ModalConfirmedResult<Date>>(
          (result): result is ModalConfirmedResult<Date> =>
            result.resolution === ModalResolution.CONFIRMED,
        ),
        map(result => result.result),
      )
      .subscribe(value => {
        this.control.setValue(value);
        this._changeDetector.markForCheck();
      });

    return false;
  }

  // ControlValueAccessor

  public writeValue(obj: unknown): void {
    this._recentlyWrittenValue = obj;
    this.control.setValue(obj);
    this._changeDetector.detectChanges();
  }

  public registerOnChange(fn: (value: unknown) => void): void {
    // FormControl has a `registerOnChange` function, but that's meant for applying model changes to
    // the view. Its listeners never get called when the view triggers the change to the model. In
    // our case the registered listener doesn't get called when the user types a value in the masked
    // input, which renders the component unusable with a keyboard.

    this.control.valueChanges.pipe(takeUntilDestroyed(this)).subscribe((value: unknown) => {
      if (this._recentlyWrittenValue !== value) {
        fn(value);
      }

      this._recentlyWrittenValue = NO_WRITTEN_VALUE;
    });
  }

  public registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    if (this._disabled === isDisabled) {
      return;
    }

    if (isDisabled) {
      this.control.disable();
    } else {
      this.control.enable();
    }

    this._disabled = isDisabled;
    this._changeDetector.detectChanges();
  }

  // Validator API

  public validate(control: AbstractControl | null): ValidationErrors | null {
    if (control == null) {
      return null;
    }

    const {value} = control as {value: Date | string | null | undefined};

    // treat empty date input as valid
    if (value == null || value === '') {
      return null;
    }

    if (!isValidDate(value)) {
      return validateDate(value);
    }

    return this._minimumValidator(control) || this._maximumValidator(control);
  }

  public registerOnValidatorChange(onValidatorChange: () => void): void {
    this._onValidatorChanged = onValidatorChange;
  }
}
